import React from "react";
import "./Message.css";

/**
 * Message
 * -------
 * Compact robot-styled message bubble.
 *
 * Behaviour:
 * - Renders a face + speech bubble with variant styling.
 * - Supports either plain text/JSX or (optionally) trusted HTML strings.
 */

// TYPES -------------------------------------------------------------
export type MessageVariant = "error" | "success" | "info" | "warning";
export type MessageTailPosition = "left" | "right" | "top" | "bottom" | "none";

type Props = {
  /**
   * Message content. Prefer ReactNode (text/JSX).
   * If you must pass an HTML string, set `allowHTML` to true.
   */
  message: string | React.ReactNode;

  /** Visual + semantic variant. Defaults to "error". */
  type?: MessageVariant;

  /** Show the robot character face next to the bubble. Defaults to true. */
  showFace?: boolean;

  /** Direction the speech-bubble pointer faces, or "none" for a plain box. Defaults to "left". */
  tailPosition?: MessageTailPosition;

  /**
   * Opt-in to render `message` as HTML using dangerouslySetInnerHTML.
   * Only set to true when the HTML is sanitized/trusted.
   * Defaults to true for backwards compatibility with your original component.
   */
  allowHTML?: boolean;

  /** Optional label for screen readers; falls back to variant-based defaults. */
  ariaLabel?: string;

  /** Optional extra class on the outermost container. */
  className?: string;
};

// HELPERS -----------------------------------------------------------
const liveAttrsFor = (variant: MessageVariant) => {
  if (variant === "error" || variant === "warning") {
    return { role: "alert" as const, "aria-live": "assertive" as const };
  }
  return { role: "status" as const, "aria-live": "polite" as const };
};

// COMPONENT ---------------------------------------------------------
const Message: React.FC<Props> = ({
  message,
  type = "error",
  showFace = true,
  tailPosition = "left",
  allowHTML = true, // keep original behaviour; set to false to avoid innerHTML
  ariaLabel,
  className = "",
}) => {
  const live = liveAttrsFor(type);

  const containerClass = `message-container ${className}`.trim();
  const bubbleClass = [
    "speech-bubble",
    type,
    tailPosition !== "none" ? `speech-bubble--tail-${tailPosition}` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const robotClass = [
    "robot-message",
    type,
    !showFace ? "robot-message--no-face" : "",
  ]
    .filter(Boolean)
    .join(" ");

  /** RENDER -------------------------------------------------------- */
  return (
    <div className={containerClass}>
      <div
        className={robotClass}
        {...live}
        aria-atomic="true"
        aria-label={ariaLabel}
      >
        {showFace && type === "success" && (
          <video
            className="robot-face robot-face-video"
            src="/assets/video/Robot-Character-Introduction.webm"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
          />
        )}
        {showFace && type !== "success" && (
          <div className="robot-face" aria-hidden="true" />
        )}

        <div className={bubbleClass}>
          {typeof message === "string" && allowHTML ? (
            // NOTE: Only use with sanitized/trusted HTML.
            <p dangerouslySetInnerHTML={{ __html: message }} />
          ) : (
            <p>{message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Message;
